import { captureMessage } from './sentry';
import { extractErrorMessage } from './classifyMediaError';

export type MediaPermissionState = PermissionState | 'unsupported';

export interface PermissionCheckResult {
  camera: MediaPermissionState;
  microphone: MediaPermissionState;
}

// This helper runs on preflight mount, Zoom start, and every retry. On a browser
// that consistently fails the microphone query (Safari/older Firefox), the
// failure is identical every time, so log it at most once per page load — enough
// to know the browser is affected without flooding Sentry with duplicates.
let micQueryFailureLogged = false;

/** Test-only: resets the once-per-session mic-query-failure log guard between cases. */
export function __resetMicQueryFailureLogForTests(): void {
  micQueryFailureLogged = false;
}

function reportMicQueryFailure(err: unknown): void {
  // Safari/older Firefox fail here ('microphone' isn't a valid PermissionName) —
  // a diagnostic failure, not a real permission state, so it's reported
  // distinctly rather than being treated as an actual denial. Logged at most
  // once per session (see above).
  if (micQueryFailureLogged) return;
  micQueryFailureLogged = true;
  captureMessage(
    'navigator.permissions.query failed for microphone',
    'camera_microphone_permission',
    { permission_name: 'microphone' },
    { error_message: extractErrorMessage(err) }
  );
}

/**
 * Queries a single permission's live PermissionStatus object once — the
 * shared primitive behind both checkMediaPermissionState() (reads `.state`
 * and discards it) and watchPermissionState() (also attaches a `change`
 * listener to the SAME object), so a caller that needs both the current
 * value and live updates never has to query twice. Never throws or rejects:
 * some browsers throw *synchronously* on an unsupported permission name
 * rather than returning a rejected promise, so this wraps both failure modes
 * the same way, returning null either way.
 */
async function queryPermissionStatus(
  name: PermissionName,
  onError?: (err: unknown) => void
): Promise<PermissionStatus | null> {
  try {
    return await navigator.permissions.query({ name });
  } catch (err) {
    onError?.(err);
    return null;
  }
}

/**
 * Queries live camera/microphone permission state without prompting the user.
 * Shared between useZoomVideo (diagnostic logging + retry re-check) and
 * useDeviceCheck (the pre-flight priming gate's one-shot post-failure
 * re-check) so both read permission state the same way. Guaranteed to
 * resolve (never reject) — returns null only when the Permissions API is
 * entirely absent.
 */
export async function checkMediaPermissionState(): Promise<PermissionCheckResult | null> {
  if (!navigator.permissions?.query) return null;

  const [camera, microphone] = await Promise.all([
    queryPermissionStatus('camera' as PermissionName),
    queryPermissionStatus('microphone' as PermissionName, reportMicQueryFailure),
  ]);

  return { camera: camera?.state ?? 'unsupported', microphone: microphone?.state ?? 'unsupported' };
}

export type PermissionWatchUnsubscribe = () => void;

/**
 * Reports a single permission's current state via `onUpdate`, then keeps
 * reporting it live — the browser firing a 'change' event whenever it
 * changes (e.g. the user updates it in their browser's site settings while
 * the page is still open). This is what lets the pre-flight screen recover
 * automatically from a prior denial instead of requiring the user to know to
 * manually reload. Reuses the same once-per-session mic-query-failure
 * diagnostic as checkMediaPermissionState() above.
 *
 * Calls `onUpdate('unsupported')` once and stops when the Permissions API —
 * or this specific permission name — isn't supported (e.g. Safari/older
 * Firefox's 'microphone'): nothing can ever "change" for a permission the
 * browser can't query to begin with. Never throws.
 */
export function watchPermissionState(
  name: PermissionName,
  onUpdate: (state: MediaPermissionState) => void
): PermissionWatchUnsubscribe {
  let status: PermissionStatus | null = null;
  let cancelled = false;

  const handleChange = () => {
    if (status) onUpdate(status.state);
  };

  queryPermissionStatus(name, name === ('microphone' as PermissionName) ? reportMicQueryFailure : undefined).then(
    (result) => {
      if (cancelled) return;
      if (!result) {
        onUpdate('unsupported');
        return;
      }
      status = result;
      onUpdate(status.state);
      if (typeof status.addEventListener === 'function') {
        status.addEventListener('change', handleChange);
      }
    }
  );

  return () => {
    cancelled = true;
    if (typeof status?.removeEventListener === 'function') {
      status.removeEventListener('change', handleChange);
    }
  };
}
